/**
 * 貼上內容清理 — 供 main.js 的 html-editor(summernote) onPaste 使用
 *
 * 為什麼需要：
 *   從 Word / Pages / 網頁貼進來的 HTML 會在每個元素上帶大量 inline 樣式，例如
 *     font-size: 12px; font-family: Times; color: rgb(0, 0, 0);
 *     -webkit-text-stroke: 0px rgb(0,0,0); letter-spacing: normal; white-space: normal;
 *   造成兩個實際問題：
 *     1. 後台深色模式：子元素自帶 color 不繼承祖先，深色底配黑字看不見
 *     2. 公開頁：社群分享頁以 th:utext 直出這段 HTML，inline 樣式優先權高於
 *        容器的 Tailwind prose，文章會變成 12px 的 Times
 *
 * 為什麼可以整個 style 拿掉而不是只挑幾個屬性：
 *   這個編輯器沒有任何排版功能 —— main.js 的 toolbar 是空陣列, FormEditor 的
 *   自訂工具列只有「插入超連結」與「清空」。使用者沒有管道自行設定顏色、字級、
 *   對齊, 所以內容裡的 inline 樣式 100% 來自貼上, 清掉不會損失任何刻意設定。
 *
 * 保留：標籤結構、文字、超連結 href、圖片 src。
 */

// 整個移除的標籤（貼上時夾帶, 對內文無意義且有風險）
const DROP_TAGS = ["SCRIPT", "STYLE", "META", "LINK", "TITLE", "BASE"];

// 移除的表現層屬性（<font color> 這類舊式標記）
const DROP_ATTRS = ["style", "class", "id", "color", "face", "size", "bgcolor", "background", "align", "width", "height"];

/**
 * 就地清掉一棵 DOM 底下的表現層屬性與無意義標籤。
 *
 * 抽出來是為了讓「整理內文格式」的清除排版樣式(cleanHtml.js)共用同一份規則 ——
 * 那邊手上已經有 DOM, 不需要再 parse/serialize 一次。
 *
 * @param {Element} root
 * @returns {number} 實際被改動的元素數（給整理視窗的統計用）
 */
export const stripPresentation = (root) => {
    let count = 0;
    root.querySelectorAll("*").forEach((el) => {
        if (DROP_TAGS.includes(el.tagName)) {
            el.remove();
            count++;
            return;
        }
        let hit = false;
        DROP_ATTRS.forEach((name) => {
            if (el.hasAttribute(name)) {
                el.removeAttribute(name);
                hit = true;
            }
        });
        // on* 事件屬性：內容會被公開頁以 th:utext 原樣輸出, 不讓它跟著進 DB。
        // （這只是前端衛生, 不能取代後端輸出時的過濾）
        Array.from(el.attributes).forEach((attr) => {
            if (/^on/i.test(attr.name)) {
                el.removeAttribute(attr.name);
                hit = true;
            }
        });
        if (hit) count++;
    });
    return count;
};

/**
 * 清理貼上的 HTML。
 *
 * @param {string} html clipboard 的 text/html
 * @returns {string} 清乾淨的 HTML 片段
 */
export const sanitizePastedHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    stripPresentation(doc.body);
    return doc.body.innerHTML;
};
